
import { useState, useCallback, useEffect } from 'react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'

interface Contrato {
  _id: string
  numero: string
  orcamentoId?: string
  nomeCliente: string
  contatoCliente: string
  nomeProjeto: string
  descricaoProjeto: string
  status: 'em_desenvolvimento' | 'enviado' | 'aprovado' | 'perdido'
  dataEmissao: string
  dataValidade: string
  dataFechamento?: string
  valorTotal: number
  etapas: any[]
  templateId?: string
  assinaturaEmpresa: string
  assinaturaCliente: string
  observacoes?: string
  clausulasEspeciais?: string
  condicoesGerais?: string
  creator: string
  createdAt: string
  updatedAt: string
}

interface TemplateContrato {
  _id: string
  nome: string
  categoria: string
  descricao: string
  conteudo: string
  clausulasEspeciais: string
  condicoesGerais: string
  prazoValidadePadrao: number
  ativo: boolean
  creator: string
  createdAt: string
  updatedAt: string
}

interface HistoricoContrato {
  _id: string
  contratoId: string
  acao: string
  descricao: string
  valorAnterior?: number
  valorNovo?: number
  statusAnterior?: string
  statusNovo?: string
  dataAcao: string
  usuario: string
  observacoes?: string
}

interface ContratoMetrics {
  totalContratos: number
  totalContratosAnterior: number
  valorTotal: number
  valorTotalAnterior: number
  quantidadeAprovada: number
  quantidadeAprovadaAnterior: number
  quantidadeFechada: number
  quantidadeFechadaAnterior: number
}

export const useContratos = () => {
  const [contratos, setContratos] = useState<Contrato[]>([])
  const [templates, setTemplates] = useState<TemplateContrato[]>([])
  const [historico, setHistorico] = useState<HistoricoContrato[]>([])
  const [loading, setLoading] = useState(false)

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [contratosData, templatesData, historicoData] = await Promise.all([
        lumi.entities.contratos.list(),
        lumi.entities.templates_contrato.list(),
        lumi.entities.historico_contratos.list()
      ])

      setContratos(contratosData.list || [])
      setTemplates(templatesData.list || [])
      setHistorico(historicoData.list || [])
    } catch (error: any) {
      console.error('Erro ao carregar dados de contratos:', error)
      toast.error('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [])

  // Calculate metrics with comparison to previous month
  const getMetrics = (): ContratoMetrics => {
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    
    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1
    const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear

    const currentMonthContratos = contratos.filter(contrato => {
      const date = new Date(contrato.createdAt)
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear
    })

    const previousMonthContratos = contratos.filter(contrato => {
      const date = new Date(contrato.createdAt)
      return date.getMonth() === previousMonth && date.getFullYear() === previousYear
    })

    return {
      totalContratos: currentMonthContratos.length,
      totalContratosAnterior: previousMonthContratos.length,
      valorTotal: currentMonthContratos.reduce((sum, contrato) => sum + contrato.valorTotal, 0),
      valorTotalAnterior: previousMonthContratos.reduce((sum, contrato) => sum + contrato.valorTotal, 0),
      quantidadeAprovada: currentMonthContratos.filter(contrato => contrato.status === 'aprovado').length,
      quantidadeAprovadaAnterior: previousMonthContratos.filter(contrato => contrato.status === 'aprovado').length,
      quantidadeFechada: currentMonthContratos.filter(contrato => contrato.dataFechamento).length,
      quantidadeFechadaAnterior: previousMonthContratos.filter(contrato => contrato.dataFechamento).length
    }
  }

  // Generate unique contract number
  const generateContractNumber = () => {
    const year = new Date().getFullYear()
    const count = contratos.length + 1
    return `CTR-${year}-${count.toString().padStart(3, '0')}`
  }

  // Get orçamentos aprovados for contract generation
  const getOrcamentosAprovados = async () => {
    try {
      const { list } = await lumi.entities.orcamentos.list()
      return list.filter(orc => orc.status === 'aprovado')
    } catch (error: any) {
      console.error('Erro ao buscar orçamentos aprovados:', error)
      return []
    }
  }

  // 🔥 FUNÇÃO MELHORADA: Update orçamento status to "fechado" when contract is approved
  const updateOrcamentoStatusToFechado = async (orcamentoId: string, contratoNumero: string) => {
    try {
      console.log(`🔄 Iniciando atualização do orçamento ${orcamentoId} para status "fechado"`)
      
      const now = new Date().toISOString()
      
      // 1. Primeiro, buscar o orçamento atual para verificar se existe
      const { list: orcamentos } = await lumi.entities.orcamentos.list()
      const orcamentoAtual = orcamentos.find(orc => orc._id === orcamentoId)
      
      if (!orcamentoAtual) {
        console.error(`❌ Orçamento ${orcamentoId} não encontrado`)
        return
      }

      console.log(`📋 Orçamento encontrado: ${orcamentoAtual.numero} - Status atual: ${orcamentoAtual.status}`)

      // 2. Verificar se o status já não é "fechado"
      if (orcamentoAtual.status === 'fechado') {
        console.log(`ℹ️ Orçamento ${orcamentoAtual.numero} já está com status "fechado"`)
        return
      }

      // 3. Atualizar o status do orçamento para "fechado"
      await lumi.entities.orcamentos.update(orcamentoId, {
        status: 'fechado',
        updatedAt: now
      })

      console.log(`✅ Status do orçamento ${orcamentoAtual.numero} atualizado para "fechado"`)

      // 4. Criar entrada no histórico do orçamento
      await lumi.entities.historico_orcamentos.create({
        orcamentoId: orcamentoId,
        acao: 'fechado',
        descricao: `Status alterado para "fechado" automaticamente devido à aprovação do contrato ${contratoNumero}`,
        statusAnterior: orcamentoAtual.status,
        statusNovo: 'fechado',
        dataAcao: now,
        usuario: 'Sistema Automático',
        creator: 'sistema',
        createdAt: now
      })

      console.log(`📝 Histórico do orçamento ${orcamentoAtual.numero} atualizado`)

      // 5. Criar notificação sobre a mudança de status
      await lumi.entities.notificacoes.create({
        tipo: 'orcamento_status',
        titulo: 'Orçamento Fechado Automaticamente',
        mensagem: `O orçamento ${orcamentoAtual.numero} foi fechado automaticamente devido à aprovação do contrato ${contratoNumero}`,
        orcamentoId: orcamentoId,
        lida: false,
        dataNotificacao: now,
        usuario: 'admin',
        creator: 'sistema',
        createdAt: now
      })

      console.log(`🔔 Notificação criada para orçamento ${orcamentoAtual.numero}`)

      // 6. Exibir toast de sucesso
      toast.success(`Orçamento ${orcamentoAtual.numero} fechado automaticamente!`)

    } catch (error: any) {
      console.error('❌ Erro ao atualizar status do orçamento:', error)
      toast.error('Erro ao atualizar status do orçamento')
    }
  }

  // Create contrato
  const createContrato = async (data: Omit<Contrato, '_id' | 'numero' | 'createdAt' | 'updatedAt'>) => {
    try {
      const numero = generateContractNumber()
      const now = new Date().toISOString()
      
      const contratoData = {
        ...data,
        numero,
        valorTotal: Number(data.valorTotal || 0),
        creator: 'admin',
        createdAt: now,
        updatedAt: now
      }

      const newContrato = await lumi.entities.contratos.create(contratoData)

      // Create history entry
      await lumi.entities.historico_contratos.create({
        contratoId: newContrato._id,
        acao: 'criado',
        descricao: `Contrato ${numero} criado${data.orcamentoId ? ' a partir de orçamento' : ''}`,
        dataAcao: now,
        usuario: 'Admin Sistema',
        creator: 'admin',
        createdAt: now
      })

      // 🔥 NOVA VERIFICAÇÃO: Se o contrato já for criado como aprovado, fechar o orçamento
      if (data.status === 'aprovado' && data.orcamentoId) {
        console.log(`🎯 Contrato criado com status aprovado - fechando orçamento ${data.orcamentoId}`)
        await updateOrcamentoStatusToFechado(data.orcamentoId, numero)
      }

      await fetchData()
      toast.success('Contrato criado com sucesso!')
      return newContrato
    } catch (error: any) {
      console.error('Erro ao criar contrato:', error)
      toast.error('Erro ao criar contrato')
      throw error
    }
  }

  // Update contrato
  const updateContrato = async (id: string, data: Partial<Contrato>) => {
    try {
      const contratoAtual = contratos.find(c => c._id === id)
      if (!contratoAtual) throw new Error('Contrato não encontrado')

      console.log(`🔄 Atualizando contrato ${contratoAtual.numero}`)
      console.log(`📊 Status atual: ${contratoAtual.status} → Novo status: ${data.status}`)
      console.log(`🔗 Orçamento vinculado: ${contratoAtual.orcamentoId}`)

      const now = new Date().toISOString()
      const updateData = {
        ...data,
        updatedAt: now
      }

      await lumi.entities.contratos.update(id, updateData)

      // Create history entry for significant changes
      if (data.status && data.status !== contratoAtual.status) {
        await lumi.entities.historico_contratos.create({
          contratoId: id,
          acao: data.status,
          descricao: `Status alterado de ${contratoAtual.status} para ${data.status}`,
          statusAnterior: contratoAtual.status,
          statusNovo: data.status,
          dataAcao: now,
          usuario: 'Admin Sistema',
          creator: 'admin',
          createdAt: now
        })

        // 🔥 LÓGICA PRINCIPAL: Sincronizar status do orçamento quando contrato for aprovado
        if (data.status === 'aprovado' && contratoAtual.orcamentoId) {
          console.log(`🎯 Contrato aprovado! Iniciando fechamento do orçamento ${contratoAtual.orcamentoId}`)
          await updateOrcamentoStatusToFechado(contratoAtual.orcamentoId, contratoAtual.numero)
        }
      }

      if (data.valorTotal && data.valorTotal !== contratoAtual.valorTotal) {
        await lumi.entities.historico_contratos.create({
          contratoId: id,
          acao: 'editado',
          descricao: 'Valor do contrato alterado',
          valorAnterior: contratoAtual.valorTotal,
          valorNovo: data.valorTotal,
          dataAcao: now,
          usuario: 'Admin Sistema',
          creator: 'admin',
          createdAt: now
        })
      }

      await fetchData()
      toast.success('Contrato atualizado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao atualizar contrato:', error)
      toast.error('Erro ao atualizar contrato')
      throw error
    }
  }

  // Delete contrato
  const deleteContrato = async (id: string) => {
    try {
      await lumi.entities.contratos.delete(id)
      await fetchData()
      toast.success('Contrato excluído com sucesso!')
    } catch (error: any) {
      console.error('Erro ao excluir contrato:', error)
      toast.error('Erro ao excluir contrato')
      throw error
    }
  }

  // Create template
  const createTemplate = async (data: Omit<TemplateContrato, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      const templateData = {
        ...data,
        prazoValidadePadrao: Number(data.prazoValidadePadrao || 30),
        creator: 'admin',
        createdAt: now,
        updatedAt: now
      }

      await lumi.entities.templates_contrato.create(templateData)
      await fetchData()
      toast.success('Template criado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao criar template:', error)
      toast.error('Erro ao criar template')
      throw error
    }
  }

  // Update template
  const updateTemplate = async (id: string, data: Partial<TemplateContrato>) => {
    try {
      const updateData = {
        ...data,
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.templates_contrato.update(id, updateData)
      await fetchData()
      toast.success('Template atualizado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao atualizar template:', error)
      toast.error('Erro ao atualizar template')
      throw error
    }
  }

  // Delete template
  const deleteTemplate = async (id: string) => {
    try {
      await lumi.entities.templates_contrato.delete(id)
      await fetchData()
      toast.success('Template excluído com sucesso!')
    } catch (error: any) {
      console.error('Erro ao excluir template:', error)
      toast.error('Erro ao excluir template')
      throw error
    }
  }

  // Check for expired contracts
  const getContratosExpirados = () => {
    const hoje = new Date()
    return contratos.filter(contrato => {
      const dataValidade = new Date(contrato.dataValidade)
      return dataValidade < hoje && contrato.status !== 'aprovado' && contrato.status !== 'perdido'
    })
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    contratos,
    templates,
    historico,
    loading,
    metrics: getMetrics(),
    fetchData,
    createContrato,
    updateContrato,
    deleteContrato,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getOrcamentosAprovados,
    getContratosExpirados,
    generateContractNumber
  }
}
